<template>
    <Toaster></Toaster>
    <Dialog :open="openModal" @update:open="(e) => {
        openModal = e
        if (!e) {
            redirectToHome()
        }
    }">
        <DialogContent>
            <DialogHeader>
                <DialogTitle>{{ model.translations['registerTree.Form.NoCollectionInArea.ThankYouTitle'] }}
                </DialogTitle>
                <DialogDescription>
                    {{ model.translations["registerTree.Form.NoCollectionInArea.ThankYouText"] }}
                </DialogDescription>
            </DialogHeader>

            <DialogFooter>
                <DialogClose as-child>
                    <Button type="button">
                        Ok
                    </Button>
                </DialogClose>
            </DialogFooter>
        </DialogContent>
    </Dialog>
    <form @submit.prevent="onSubmit" class="registration-close-form" ref="formSection">
        <div v-if="collectionLookup.collectionLookupResult.value === CollectionLookup.REGISTRATION_CLOSED" class="mb-3"
            v-html="model.headingHtml">
        </div>
        <div v-if="collectionLookup.collectionLookupResult.value === CollectionLookup.REGISTRATION_FULL" class="mb-3"
            v-html="collectionFullText"></div>
        <h5>{{ model.translations["registerTree.Form.NoCollectionInArea.HowToContact"] }}</h5>
        <h2>{{ model.translations['registerTree.Form.NoCollectionInArea.AdditionalContactInfo'] }}</h2>
        <FormField name="contactMethods">
            <FormItem>
                <FormField v-for="item in model.contactMethods" v-slot="{ value, handleChange }" :key="item.value"
                    type="checkbox" :value="item.value" :unchecked-value="false" name="contactMethods">
                    <FormItem class="checkbox-field">
                        <FormControl>
                            <Checkbox :model-value="value.includes(item.value)" @update:model-value="handleChange" />
                        </FormControl>
                        <FormLabel>
                            {{ item.text }}
                        </FormLabel>
                    </FormItem>
                </FormField>
                <FormMessage />
            </FormItem>
        </FormField>
        <template v-if="form.values.contactMethods?.length">
            <h2>Contact Details</h2>
            <div class="field-group">
                <FormField v-slot="{ componentField }" name="phone"
                    v-if="form.values.contactMethods.includes('Phone') || form.values.contactMethods.includes('Text')">
                    <FormItem>
                        <FormControl>
                            <VueTelInput type="number"
                                :placeholder="model.translations['generic.PersonalDetails.PhoneNumber']"
                                v-bind="componentField" class="input" defaultCountry="GB" />
                        </FormControl>
                    </FormItem>
                </FormField>
                <FormField v-slot="{ componentField }" name="email" v-if="form.values.contactMethods.includes('Email')">
                    <FormItem>
                        <FormControl>
                            <Input type="text" :placeholder="model.translations['generic.PersonalDetails.Email']"
                                v-bind="componentField" />
                        </FormControl>
                    </FormItem>
                </FormField>
            </div>
        </template>
        <FormField v-slot="{ componentField }" name="additionalInformation">
            <FormItem>
                <FormControl>
                    <Input type="text" placeholder="Details" v-bind="componentField" />
                </FormControl>
            </FormItem>
        </FormField>
        <FormField v-slot="{ componentField }" name="leadSource">
            <FormItem>
                <FormControl>
                    <FormLabel>
                        {{ model.translations["registerTree.Form.NoCollectionInArea.LeadSource"] }}
                    </FormLabel>
                    <Select v-bind="componentField">
                        <FormControl>
                            <SelectTrigger class="w-full">
                                <SelectValue placeholder="Select" />
                            </SelectTrigger>
                        </FormControl>
                        <SelectContent class="max-h-[400px]">
                            <SelectGroup>
                                <SelectItem v-for="item in model.leadSources" :value="item.value">
                                    {{ item.text }}
                                </SelectItem>
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                </FormControl>
            </FormItem>
        </FormField>
        <template v-if="collectionLookup.hospices?.value?.length">
            <div>{{ model.translations["registerTree.Form.NoCollectionInArea.WhoCollects"] }}</div>
            <div v-for="(hospice, $index) in collectionLookup.hospices.value" :key="$index">
                <a :href="hospice.url" target="_blank">{{ hospice.name }}</a>
            </div>
        </template>
        <div class="buttons">
            <Button type="submit">
                {{ model.translations["registerTree.Form.NoCollectionInArea.SubmitButtonText"] }}
            </Button>
        </div>
    </form>
</template>
<script lang="ts" setup>
import { computed, inject, onMounted, ref } from 'vue';
import { DEFAULT_POST_CODE_DATA } from '../TreeRegistration.constant';
import { PostCodeDataKey } from '../TreeRegistration.model';
import type { IRegistrationCloseFormProps } from './RegistrationCloseForm.model';
import { CollectionLookup } from '../TreeRegistration.model';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm } from 'vee-validate';
import { load, type ReCaptchaInstance } from 'recaptcha-v3';
import { toTypedSchema } from '@vee-validate/zod';
import * as z from 'zod';
import { Checkbox } from "@/components/ui/checkbox";
import { VueTelInput } from 'vue-tel-input';
import { toast } from 'vue-sonner';
import { Toaster } from '@/components/ui/sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useMutation } from '@tanstack/vue-query';
import { subscribeChristmasTree, type ISubscribeChristmasTreeRequest } from '@/api/registerTree';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const { model } = defineProps<IRegistrationCloseFormProps>();
let recaptcha: ReCaptchaInstance;
const collectionLookup = inject(PostCodeDataKey, DEFAULT_POST_CODE_DATA)
const openModal = ref(false);
const formSection = ref<HTMLElement | null>(null)

onMounted(() => {
    if (!model.reCaptcha?.siteKey) {
        toast("Can't initialize captcha", { important: true })
        return;
    }
    load(model.reCaptcha?.siteKey ?? "").then(async (recaptchaInstance) => {
        recaptcha = recaptchaInstance
    })
    if (formSection.value) {
        formSection.value.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
})

const formSchema = toTypedSchema(z.object({
    contactMethods: z.array(z.string()).refine(value => value.some(item => item), {
        message: "You have to select at least one item.",
    }),
    leadSource: z.string().optional(),
    additionalInformation: z.string().optional(),
    phone: z.string().optional(),
    email: z.email().optional(),
}).refine((data) => {
    if ((data.contactMethods.includes("Phone") || data.contactMethods.includes("text")) && !data.phone) return false;
    if (data.contactMethods.includes("Email") && !data.email) return false;
    return true;
}))

const { mutate: subscribeTreeSubmit } = useMutation({
    mutationFn: (data: ISubscribeChristmasTreeRequest) => subscribeChristmasTree(model.submitUrl ?? "", data),
    onError: () => {
        console.log(model.translations["generic.Error"])
        toast("We couldn't sent your request", { important: true })
    },
    onSuccess: () => {
        openModal.value = true;
    }
})

const form = useForm({
    validationSchema: formSchema,
    initialValues: {
        contactMethods: [],
    }
})

const onSubmit = form.handleSubmit((values) => {
    recaptcha.execute("register").then((token) => {
        if (!window.__STATE__) {
            return;
        }
        subscribeTreeSubmit({
            ...values,
            contactMethods: values.contactMethods.join(","),
            postCode: collectionLookup.postCode.value,
            captchaToken: token,
            culture: window.__STATE__.culture,
            currentUmbracoPageId: window.__STATE__.currentPageId
        })
    })
})

const collectionFullText = computed(() => {
    return model.collectionFullHeadingHtml.replace("{collection}", collectionLookup.hospices?.value?.[0].name ?? "")
})

const redirectToHome = () => {
    window.location.href = '/';
}
</script>
<style lang="scss" scoped>
.form-wrapper {
    width: 100%;
}

.registration-close-form {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.checkbox-field {
    display: flex;
    gap: 10px;
    align-items: center;
}

.buttons {
    display: flex;
    justify-content: flex-end;
}

.field-group {
    >div {
        flex: 1
    }

    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.input {
    border-color: var(--border);
    border-radius: calc(var(--radius) - 2px);
}

@media (min-width: 768px) {
    .field-group {
        flex-direction: row;
    }
}
</style>